import { useCallback, useMemo } from 'react';
import { UNSEATED_GROUP_ID } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/constants';
import { useTableSeatingStore } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/store';

const useSelectionOperations = () => {
    const {
        tickets,
        selectedItems,
        showCheckboxes,
        toggleItemSelection,
        toggleGroupSelection,
        clearSelection,
        collapsedGroups,
        toggleGroupCollapse,
        unseatMultiple,
        takeSnapshot,
    } = useTableSeatingStore(state => ({
        tickets: state.tickets,
        selectedItems: state.selectedItems,
        showCheckboxes: state.showCheckboxes,
        toggleItemSelection: state.toggleItemSelection,
        toggleGroupSelection: state.toggleGroupSelection,
        clearSelection: state.clearSelection,
        collapsedGroups: state.collapsedGroups,
        toggleGroupCollapse: state.toggleGroupCollapse,
        unseatMultiple: state.unseatMultiple,
        takeSnapshot: state.takeSnapshot,
    }));

    const selectedTickets = useMemo(() => {
        return Array.from(selectedItems)
            .map(id => tickets[id])
            .filter(Boolean);
    }, [selectedItems, tickets]);

    const hasGroups = useMemo(() => {
        return selectedTickets.some(ticket => ticket.ticket_group_id !== null);
    }, [selectedTickets]);

    const allUnseated = useMemo(() => {
        return (
            selectedTickets.length > 0 &&
            selectedTickets.every(ticket => ticket.seating_group_id === UNSEATED_GROUP_ID)
        );
    }, [selectedTickets]);

    const allSeated = useMemo(() => {
        return (
            selectedTickets.length > 0 &&
            selectedTickets.every(ticket => ticket.seating_group_id !== UNSEATED_GROUP_ID)
        );
    }, [selectedTickets]);

    const unseatSelection = useCallback(() => {
        if (selectedTickets.length === 0) return;
        takeSnapshot();
        const ticketIdsToUnseat = selectedTickets
            .filter(ticket => ticket.seating_group_id !== UNSEATED_GROUP_ID)
            .map(ticket => ticket.id);
        if (ticketIdsToUnseat.length > 0) {
            unseatMultiple(ticketIdsToUnseat);
        }
        clearSelection();
    }, [selectedTickets, unseatMultiple, clearSelection, takeSnapshot]);

    const isSelected = (itemId: string) => selectedItems.has(itemId);

    const isGroupCollapsed = (groupId: string, tableId: string) => {
        const collapseKey = `${groupId}-${tableId}`;
        return collapsedGroups.has(collapseKey);
    };

    const toggleGroupCollapseAtTable = (groupId: string, tableId: string) => {
        toggleGroupCollapse(groupId, tableId);
    };

    const hasSelection = selectedItems.size > 0;

    return {
        selectedItems,
        selectedTickets,
        showCheckboxes,
        hasSelection,
        selectionCount: selectedItems.size,
        hasGroups,
        allUnseated,
        allSeated,
        collapsedGroups,
        //
        isSelected,
        toggleItemSelection,
        toggleGroupSelection,
        clearSelection,
        unseatSelection,
        isGroupCollapsed,
        toggleGroupCollapseAtTable,
    };
};

export { useSelectionOperations };
